import { CoolHttp } from '@angular-cool/http';
import { Injectable } from '@angular/core';
import { CacheState, CacheStateUpdater } from '@coool/cachestate';
import { Observable } from 'rxjs';
import {
  KnowledgeBaseArticleDTO,
  KnowledgeBaseArticleDTOMinimal,
  KnowledgeBaseArticleId,
  KnowledgeBaseArticleListRequestParams,
  KnowledgeBaseCategoryDTO,
  KnowledgeBaseCategoryId,
  KnowledgeBaseDomainDTO,
  KnowledgeBaseDomainId,
  KnowledgeBaseDTO,
  KnowledgeBaseDTOMinimal,
  KnowledgeBaseFeaturedDTO,
  KnowledgeBaseFeaturedId,
  KnowledgeBaseId,
} from '../../../../../common/dto/knowledge-base.dto';
import { ServerUrls } from '../../../../../common/dto/server-urls';
import { FileUploadRequestUtils } from '../utils/file-upload-request.utils';

@Injectable()
export class KnowledgeBaseService {
  constructor(
    private _http: CoolHttp,
  ) {
  }

  @CacheStateUpdater({
    updatedNotifierKey: 'knowledgeBase',
  })
  public async upsertKnowledgeBaseAsync(knowledgeBase: KnowledgeBaseDTO): Promise<KnowledgeBaseDTO> {
    const formData = FileUploadRequestUtils.createRequestForm('knowledgeBase', knowledgeBase, [
      {
        name: 'logo',
        resolver: _ => _.logo,
      },
      {
        name: 'favicon',
        resolver: _ => _.favicon,
      },
      {
        name: 'heroImage',
        resolver: _ => _.heroImage,
      },
    ]);

    return await this._http.postAsync(ServerUrls.KnowledgeBases._.Upsert.absoluteUrl, formData);
  }

  @CacheState({
    updatedObservableKey: 'knowledgeBase',
  })
  public getKnowledgeBases(): Observable<KnowledgeBaseDTOMinimal[]> {
    return this._http.getObservable(ServerUrls.KnowledgeBases._.Get.absoluteUrl);
  }

  @CacheState({
    updatedObservableKey: 'knowledgeBase',
  })
  public getKnowledgeBaseByIdCACHED(knowledgeBaseId: KnowledgeBaseId): Observable<KnowledgeBaseDTO> {
    return this._http.getObservable(ServerUrls.KnowledgeBases._.ById._.Get.getAbsoluteUrlWithParams({
      [ServerUrls.KnowledgeBases._.ById.params.knowledgeBaseId]: knowledgeBaseId,
    }));
  }

  @CacheStateUpdater({
    updatedNotifierKey: 'knowledgeBase',
  })
  public async removeKnowledgeBaseAsync(knowledgeBaseId: KnowledgeBaseId) {
    return await this._http.deleteAsync(ServerUrls.KnowledgeBases._.ById._.Delete.getAbsoluteUrlWithParams({
      [ServerUrls.KnowledgeBases._.ById.params.knowledgeBaseId]: knowledgeBaseId,
    }));
  }

  public async getKnowledgeBaseCategoriesAsync(knowledgeBaseId: KnowledgeBaseId): Promise<KnowledgeBaseCategoryDTO[]> {
    return this._http.getAsync(ServerUrls.KnowledgeBases._.ById._.Categories._.Get.getAbsoluteUrlWithParams({
      [ServerUrls.KnowledgeBases._.ById.params.knowledgeBaseId]: knowledgeBaseId,
    }));
  }

  public async getKnowledgeBaseDomainsAsync(knowledgeBaseId: KnowledgeBaseId): Promise<KnowledgeBaseDomainDTO[]> {
    return this._http.getAsync(ServerUrls.KnowledgeBases._.ById._.Domains._.Get.getAbsoluteUrlWithParams({
      [ServerUrls.KnowledgeBases._.ById.params.knowledgeBaseId]: knowledgeBaseId,
    }));
  }

  public async addKnowledgeBaseDomainsAsync(knowledgeBaseId: KnowledgeBaseId, domain: KnowledgeBaseDomainDTO): Promise<KnowledgeBaseDomainDTO> {
    return this._http.postAsync<KnowledgeBaseDomainDTO, KnowledgeBaseDomainDTO>(ServerUrls.KnowledgeBases._.ById._.Domains._.Create.getAbsoluteUrlWithParams({
      [ServerUrls.KnowledgeBases._.ById.params.knowledgeBaseId]: knowledgeBaseId,
    }), domain);
  }

  public async removeKnowledgeBaseDomainByIdAsync(knowledgeBaseId: KnowledgeBaseId, domainId: KnowledgeBaseDomainId) {
    return this._http.deleteAsync(ServerUrls.KnowledgeBases._.ById._.Domains._.ById._.Delete.getAbsoluteUrlWithParams({
      [ServerUrls.KnowledgeBases._.ById.params.knowledgeBaseId]: knowledgeBaseId,
      [ServerUrls.KnowledgeBases._.ById._.Domains._.ById.params.domainId]: domainId,
    }));
  }

  public async upsertKnowledgeBaseCategoryAsync(knowledgeBaseId: KnowledgeBaseId, category: KnowledgeBaseCategoryDTO) {
    return this._http.postAsync<KnowledgeBaseCategoryDTO, KnowledgeBaseCategoryDTO>(ServerUrls.KnowledgeBases._.ById._.Categories._.Upsert.getAbsoluteUrlWithParams({
      [ServerUrls.KnowledgeBases._.ById.params.knowledgeBaseId]: knowledgeBaseId,
    }), category);
  }

  public async removeKnowledgeBaseCategoryByIdAsync(knowledgeBaseId: KnowledgeBaseId, categoryId: KnowledgeBaseCategoryId) {
    return this._http.deleteAsync(ServerUrls.KnowledgeBases._.ById._.Categories._.ById._.Delete.getAbsoluteUrlWithParams({
      [ServerUrls.KnowledgeBases._.ById.params.knowledgeBaseId]: knowledgeBaseId,
      [ServerUrls.KnowledgeBases._.ById._.Categories._.ById.params.categoryId]: categoryId,
    }));
  }

  public async getArticlesAsync(params: KnowledgeBaseArticleListRequestParams): Promise<KnowledgeBaseArticleDTOMinimal[]> {
    return await this._http.getAsync(ServerUrls.Articles._.Get.absoluteUrl, {
      params: {
        [ServerUrls.Articles._.Get.queryParams.limit]: params.limit?.toString() ?? '',
        [ServerUrls.Articles._.Get.queryParams.skip]: params.skip?.toString() ?? '',
        [ServerUrls.Articles._.Get.queryParams.filter]: params.filter ?? '',
      },
    });
  }

  public async getArticleByIdAsync(articleId: KnowledgeBaseArticleId): Promise<KnowledgeBaseArticleDTO> {
    return this._http.getAsync(ServerUrls.Articles._.ById._.Get.getAbsoluteUrlWithParams({
      [ServerUrls.Articles._.ById.params.articleId]: articleId,
    }));
  }

  public async upsertArticleAsync(article: KnowledgeBaseArticleDTO): Promise<KnowledgeBaseArticleDTO> {
    return this._http.postAsync<KnowledgeBaseArticleDTO, KnowledgeBaseArticleDTO>(ServerUrls.Articles._.Upsert.absoluteUrl, article);
  }

  public async removeArticleByIdAsync(articleId: KnowledgeBaseArticleId) {
    return this._http.deleteAsync(ServerUrls.Articles._.ById._.Delete.getAbsoluteUrlWithParams({
      [ServerUrls.Articles._.ById.params.articleId]: articleId,
    }));
  }

  public async getKnowledgeBaseCategoriesByIdsAsync(categoryIds: KnowledgeBaseCategoryId[]): Promise<KnowledgeBaseCategoryDTO[]> {
    return this._http.getAsync(ServerUrls.KnowledgeBaseCategories._.Get.getAbsoluteUrlWithQuery({
      [ServerUrls.KnowledgeBaseCategories._.Get.queryParams.ids]: categoryIds.join(','),
    }));
  }

  public async getFeaturedAsync(knowledgeBaseId: KnowledgeBaseId): Promise<KnowledgeBaseFeaturedDTO[]> {
    return this._http.getAsync(ServerUrls.KnowledgeBases._.ById._.Featured._.Get.getAbsoluteUrlWithParams({
      [ServerUrls.KnowledgeBases._.ById.params.knowledgeBaseId]: knowledgeBaseId,
    }));
  }

  public async upsertFeaturedAsync(knowledgeBaseId: KnowledgeBaseId, featured: KnowledgeBaseFeaturedDTO): Promise<KnowledgeBaseFeaturedDTO> {
    const formData = FileUploadRequestUtils.createRequestForm('featured', featured, [
      {
        name: 'image',
        resolver: _ => _.image,
      },
    ]);

    return await this._http.postAsync(
      ServerUrls.KnowledgeBases._.ById._.Featured._.Upsert.getAbsoluteUrlWithParams({
        [ServerUrls.KnowledgeBases._.ById.params.knowledgeBaseId]: knowledgeBaseId,
      }),
      formData,
    );
  }

  public async removeFeaturedByIdAsync(knowledgeBaseId: KnowledgeBaseId, featuredId: KnowledgeBaseFeaturedId): Promise<void> {
    return this._http.deleteAsync(ServerUrls.KnowledgeBases._.ById._.Featured._.ById._.Delete.getAbsoluteUrlWithParams({
      [ServerUrls.KnowledgeBases._.ById.params.knowledgeBaseId]: knowledgeBaseId,
      [ServerUrls.KnowledgeBases._.ById._.Featured._.ById.params.featuredId]: featuredId,
    }));
  }
}
